// src/components/listing-selection-modal/ListingSelectionModal.js
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSystemNotification } from '../../store/system/system.action';
import { useGetSalesQuery, useAddCardToSaleMutation } from '../../entities/sales/sales.api';
import { selectUser } from '../../store/user/user.selector';
import { isSold } from '../../utils/sales/sales.constants';
import CardAttribute from '../card-attribute/CardAttribute';
import addIcon from '../../assets/system/icons/add-icon.png';
import minusIcon from '../../assets/system/icons/minus-icon.png';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  SetsList,
  SetItem,
  SetCheckbox,
  SetName,
  SetDescription,
  SetStats,
  ModalFooter,
  ButtonGroup,
  AddButton,
  CancelButton,
  LoadingSpinner,
  ErrorMessage,
  VariationsSection,
  VariationItem,
  VariationCheckbox,
  VariationInfo,
  VariationName,
  QuantityControls,
  QtyButton,
  QtyValue
} from './ListingSelectionModal.styles';

export default function ListingSelectionModal({ 
  isOpen, 
  onClose, 
  cardData, 
  onSuccess 
}) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [selectedSales, setSelectedSales] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState({}); // { variationKey: { selected: boolean, quantity: number } }
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { data: allSales = [], isLoading, error } = useGetSalesQuery(
    { uid: user?.uid },
    { skip: !user?.uid }
  );
  const [addCardToSale] = useAddCardToSaleMutation();

  // A sold sale's cards have already been released from the collection, so
  // adding to one would put it out of step with what was actually sold.
  const sales = allSales.filter((sale) => !isSold(sale.status));

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Initialize variation selection when modal opens
  useEffect(() => {
    if (isOpen && cardData) {
      setSelectedSales([]);
      
      // Get all available variations (always show all collected variations, including BASE)
      const variations = cardData.variations || [];
      const preSelectedVariationName = cardData.preSelectedVariation; // Name of variation to pre-select
      
      const initialSelection = {};
      
      // Always show all variations (BASE is just another variation)
      variations.forEach((variation, index) => {
        const variationKey = variation.cardParallelName || `variation_${index}`;
        const shouldPreSelect = preSelectedVariationName 
          ? variation.cardParallelName === preSelectedVariationName
          : true; // If no pre-selection specified, select all (from collapsed view)
        
        // Get the maximum available quantity (what the user has collected)
        const maxQuantity = variation.cardQty || 1;
        
        initialSelection[variationKey] = {
          selected: shouldPreSelect,
          quantity: 1,
          maxQuantity: maxQuantity, // Store max available
          variation: variation
        };
      });
      
      setSelectedVariations(initialSelection);
    }
  }, [isOpen, cardData]);

  const handleSaleToggle = (saleId) => {
    setSelectedSales(prev =>
      prev.includes(saleId)
        ? prev.filter(id => id !== saleId)
        : [...prev, saleId]
    );
  };

  const handleVariationToggle = (variationKey) => {
    setSelectedVariations(prev => ({
      ...prev,
      [variationKey]: {
        ...prev[variationKey],
        selected: !prev[variationKey]?.selected
      }
    }));
  };

  const handleQuantityChange = (variationKey, delta) => {
    setSelectedVariations(prev => {
      const current = prev[variationKey];
      if (!current) return prev;
      
      const currentQty = current.quantity || 1;
      const maxQty = current.maxQuantity || 1;
      const newQuantity = Math.max(1, Math.min(maxQty, currentQty + delta));
      
      return {
        ...prev,
        [variationKey]: {
          ...current,
          quantity: newQuantity
        }
      };
    });
  };

  // Get all available variations (always from variations array, not single variation)
  const getAvailableVariations = () => {
    if (cardData?.variations && cardData.variations.length > 0) {
      return cardData.variations;
    }
    return [];
  };

  const availableVariations = getAvailableVariations();
  const hasVariations = availableVariations.length > 0;

  // Calculate total quantity of selected variations
  const calculateTotalQuantity = () => {
    return Object.values(selectedVariations).reduce((total, item) => {
      return total + (item.selected ? (item.quantity || 1) : 0);
    }, 0);
  };

  // Helper function to get card data for listing
  const getCardDataForListing = (variationData) => {
    const checklistPath = cardData.checklistPath || '';
    const isCollected = cardData.isCollected !== undefined 
      ? cardData.isCollected 
      : (availableVariations.length > 0);

    const baseCardData = {
      cardNumber: cardData.cardNumber,
      name: cardData.name,
      team: cardData.team || [],
      cardAttributes: cardData.cardAttributes || [],
      checklistPath,
      isCollected: isCollected,
      quantity: 1 // Always 1 per document
    };

    // All items should have a variation (BASE is just another variation)
    if (variationData.variation) {
      return {
        ...baseCardData,
        variation: {
          cardParallelName: variationData.variation.cardParallelName,
          cardColor: variationData.variation.cardColor,
          cardQty: 1, // Always 1 per document
          cardPrintNumber: variationData.variation.cardPrintNumber,
          cardSupply: variationData.variation.cardSupply
        }
      };
    } else {
      // Fallback if no variation (shouldn't happen, but handle gracefully)
      return baseCardData;
    }
  };

  const handleAddToSales = async () => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Please sign in to add to sales'));
      return;
    }

    if (selectedSales.length === 0) {
      dispatch(setSystemNotification('Select at least one sale'));
      return;
    }

    // Get all selected variations with their quantities
    const selectedItems = Object.entries(selectedVariations)
      .filter(([_, item]) => item.selected === true)
      .map(([_, item]) => item);

    if (selectedItems.length === 0) {
      dispatch(setSystemNotification('Select at least one variation'));
      return;
    }

    setIsSubmitting(true);
    try {
      const addPromises = [];
      
      selectedSales.forEach(saleId => {
        selectedItems.forEach(item => {
          const quantity = item.quantity || 1;
          for (let i = 0; i < quantity; i++) {
            const cardDataForListing = getCardDataForListing(item);
            cardDataForListing.quantity = 1;
            addPromises.push(
              addCardToSale({ uid: user.uid, saleId, cardData: cardDataForListing }).unwrap()
            );
          }
        });
      });

      await Promise.all(addPromises);
      onSuccess?.(selectedSales);
      onClose();
    } catch (error) {
      dispatch(setSystemNotification('Error adding card to sales'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const totalQuantity = calculateTotalQuantity();
  const hasSelectedVariations = Object.values(selectedVariations).some(item => item.selected);


  const modalContent = (
    <ModalOverlay onClick={onClose} className='widget-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add to Sales</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {isLoading && <LoadingSpinner>Loading sales...</LoadingSpinner>}
          
          {error && <ErrorMessage>Error loading sales: {error.message}</ErrorMessage>}
          
          {sales.length === 0 && !isLoading && (
            <ErrorMessage>
              No active sales available. Create a sale first!
            </ErrorMessage>
          )}

          {sales.length > 0 && (
            <>
              {/* Variations Selection Section */}
              {hasVariations && (
                <VariationsSection>
                  <p style={{ marginBottom: '12px', fontWeight: '600' }}>Select Variations:</p>
                  <SetsList>
                    {availableVariations.map((variation, index) => {
                      const variationKey = variation.cardParallelName || `variation_${index}`;
                      const selection = selectedVariations[variationKey] || { selected: false, quantity: 1, variation };
                      const variationName = variation.cardParallelName || 'Base';
                      const printInfo = variation.cardPrintNumber && variation.cardSupply 
                        ? ` (${variation.cardPrintNumber}/${variation.cardSupply})` 
                        : '';

                      return (
                        <VariationItem key={variationKey}>
                          <VariationCheckbox
                            type="checkbox"
                            checked={selection.selected}
                            onChange={() => handleVariationToggle(variationKey)}
                          />
                          <VariationInfo>
                            <VariationName>
                              <CardAttribute
                                color={variation.cardColor || '#FFFFFF'}
                                text={`${variationName}${printInfo}`}
                                size="M"
                              />
                            </VariationName>
                            {selection.selected && (
                              <QuantityControls>
                                <QtyButton 
                                  onClick={() => handleQuantityChange(variationKey, -1)}
                                  disabled={selection.quantity <= 1}
                                >
                                  <img src={minusIcon} alt="decrease quantity" />
                                </QtyButton>
                                <QtyValue>
                                  {selection.quantity}
                                  {selection.maxQuantity && (
                                    <span style={{ 
                                      fontSize: '10px', 
                                      opacity: 0.7, 
                                      marginLeft: '4px' 
                                    }}>
                                      /{selection.maxQuantity}
                                    </span>
                                  )}
                                </QtyValue>
                                <QtyButton 
                                  onClick={() => handleQuantityChange(variationKey, 1)}
                                  disabled={selection.quantity >= (selection.maxQuantity || 1)}
                                >
                                  <img src={addIcon} alt="increase quantity" />
                                </QtyButton>
                              </QuantityControls>
                            )}
                          </VariationInfo>
                        </VariationItem>
                      );
                    })}
                  </SetsList>
                </VariationsSection>
              )}

              {/* Sales Selection Section */}
              <div style={{ marginTop: hasVariations ? '24px' : '0' }}>
                <p>
                  Select the sales you want to add this card to:
                  {totalQuantity > 0 && (
                    <span style={{ fontWeight: '600' }}>
                      {' '}({totalQuantity} card{totalQuantity !== 1 ? 's' : ''} will be added)
                    </span>
                  )}
                </p>
                <SetsList>
                  {sales.map((sale) => (
                    <SetItem key={sale.id}>
                      <SetCheckbox
                        type="checkbox"
                        checked={selectedSales.includes(sale.id)}
                        onChange={() => handleSaleToggle(sale.id)}
                      />
                      <div>
                        <SetName>{sale.title?.trim() || 'Unnamed Sale'}</SetName>
                        <SetDescription>
                          Listed: {sale.listingDate ? formatDate(sale.listingDate) : 'N/A'}
                          {sale.ebaySku ? ` · ${sale.ebaySku}` : ''}
                        </SetDescription>
                        <SetStats>
                          <span>
                            {Number(sale.cardCount) || 0} card{(Number(sale.cardCount) || 0) === 1 ? '' : 's'}
                            {' · '}
                            Asking: {formatCurrency(sale.price)}
                          </span>
                        </SetStats>
                      </div>
                    </SetItem>
                  ))}
                </SetsList>
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <AddButton 
              onClick={handleAddToSales}
              disabled={selectedSales.length === 0 || !hasSelectedVariations || isSubmitting}
            >
              {isSubmitting ? 'Adding...' : `Add to ${selectedSales.length} Sale${selectedSales.length !== 1 ? 's' : ''}`}
            </AddButton>
            <CancelButton onClick={onClose}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
